import { createContext, useContext, useEffect, useRef, useState, type ReactNode } from 'react'
import { db, initDB, resetDbConnections } from '@/lib/db'
import { recoverOrphanedDrafts } from '@/lib/sync/draftSync'
import { useToast } from '@/components/providers/ToastProvider'
import { useStore } from '@/stores'
import { clearPendingAutoReload } from '@/lib/autoReload'
import { startAutoReload, type AutoReloadHandle } from '@/lib/appReload'
import { dispatchForceLogout } from '@/lib/authEvents'
import {
  recordRecoveryRefreshAttempt,
  setForcedLogoutReason,
  wasForcedLogoutRecently,
} from '@/lib/recoveryRefreshLoop'

const IDB_HEALTH_PROBE_TIMEOUT_MS = 1500

async function probeIdbHealth(): Promise<boolean> {
  try {
    const probe = db.drafts.count()
    const timeout = new Promise<never>((_, reject) => {
      window.setTimeout(
        () => reject(new DOMException('probe timeout', 'TimeoutError')),
        IDB_HEALTH_PROBE_TIMEOUT_MS,
      )
    })
    await Promise.race([probe, timeout])
    return true
  } catch {
    return false
  }
}

type RecoveryContextValue = {
  isRecovering: boolean
}

const RecoveryContext = createContext<RecoveryContextValue | undefined>(undefined)

const RECOVERY_EVENT = 'app-recovery-required'
const RECOVERY_STOP_RECORDING_EVENT = 'app-recovery-stop-recording'
const RECORDING_SAVED_EVENT = 'recording-saved'

function isIndexedDbInternalError(error: unknown): boolean {
  if (error instanceof DOMException) {
    return error.name === 'InvalidStateError' || error.name === 'AbortError'
  }
  const message = error instanceof Error ? error.message : String(error)
  const lower = message.toLowerCase()
  return (
    (message.includes('internal') && lower.includes('indexeddb')) ||
    lower.includes('connection to indexed database server lost')
  )
}

async function runRecovery(
  setRecovering: (value: boolean) => void,
  onUnrecoverable: () => void,
): Promise<void> {
  setRecovering(true)
  let success = false
  try {
    resetDbConnections()
    for (let attempt = 0; attempt < 3; attempt += 1) {
      try {
        await initDB()
        success = true
        break
      } catch (err) {
        console.warn('Recovery attempt failed to reinitialize IndexedDB', err)
        resetDbConnections()
      }
    }
  } finally {
    setRecovering(false)
    if (!success) {
      onUnrecoverable()
    }
  }
}

export function RecoveryProvider({ children }: { children: ReactNode }) {
  const [isRecovering, setRecovering] = useState(false)
  const toast = useToast()
  const toastRef = useRef(toast)
  toastRef.current = toast
  const lastHiddenAtRef = useRef<number | null>(null)
  const recoveryInFlightRef = useRef(false)
  const pendingRefreshAfterSaveRef = useRef<{
    durationLabel: string
  } | null>(null)
  const activeReloadHandleRef = useRef<AutoReloadHandle | null>(null)

  useEffect(() => {
    const startAutoRefreshCountdown = (
      message: string,
      kind: 'recovery-failure' | 'resume-refresh' = 'resume-refresh',
      precondition?: () => Promise<boolean> | boolean,
    ) => {
      if (kind === 'recovery-failure') {
        const now = Date.now()
        if (!wasForcedLogoutRecently(now)) {
          const decision = recordRecoveryRefreshAttempt(now)
          if (decision.shouldForceLogout) {
            setForcedLogoutReason('recovery-refresh-loop')
            clearPendingAutoReload()
            pendingRefreshAfterSaveRef.current = null
            activeReloadHandleRef.current?.cancel()
            activeReloadHandleRef.current = null
            dispatchForceLogout({ reason: 'recovery-refresh-loop' })
            return
          }
        }
      }

      void startAutoReload({
        reason: kind === 'recovery-failure' ? 'recovery-failure' : 'background-health-failure',
        message,
        toast: toastRef.current,
        precondition,
        onCancel: () => {
          pendingRefreshAfterSaveRef.current = null
          activeReloadHandleRef.current = null
        },
      }).then((handle) => {
        activeReloadHandleRef.current = handle
      })
    }

    const runRecoveryOnce = async () => {
      if (recoveryInFlightRef.current) return
      recoveryInFlightRef.current = true
      try {
        await runRecovery(setRecovering, () => {
          startAutoRefreshCountdown(
            'We’re having trouble restoring storage. Refreshing in {s}s to recover.',
            'recovery-failure',
          )
        })
      } catch {
        // ignore; runRecovery handles fallback behavior
      } finally {
        recoveryInFlightRef.current = false
      }
    }

    const handleRecoveryEvent = () => {
      void runRecoveryOnce()
    }

    const handlePageShow = (event: PageTransitionEvent) => {
      if (event.persisted) {
        void runRecoveryOnce()
      }
    }

    const handleRecordingSaved = () => {
      const pending = pendingRefreshAfterSaveRef.current
      if (!pending) {
        return
      }
      const audioPlaying = Array.from(document.querySelectorAll('audio')).some(
        (el) => !el.paused && !el.ended,
      )
      const stillRecording = useStore.getState().isRecording
      if (!stillRecording && !audioPlaying) {
        pendingRefreshAfterSaveRef.current = null
        startAutoRefreshCountdown(
          `Welcome back — refreshing in {s}s. You were away for ${pending.durationLabel} and your session may have paused in the background.`,
        )
      }
    }

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        lastHiddenAtRef.current = Date.now()
        return
      }

      if (document.visibilityState === 'visible' && lastHiddenAtRef.current) {
        const now = Date.now()
        const awayMs = Math.max(0, now - lastHiddenAtRef.current)
        lastHiddenAtRef.current = null

        const seconds = Math.floor(awayMs / 1000)
        const minutes = Math.floor(seconds / 60)
        const remainingSeconds = seconds % 60
        const durationLabel =
          minutes > 0 ? `${minutes}m ${remainingSeconds}s` : `${remainingSeconds}s`

        const isRecording = useStore.getState().isRecording
        const isAudioPlaying = Array.from(document.querySelectorAll('audio')).some(
          (el) => !el.paused && !el.ended,
        )
        if (awayMs > 120_000 && !isRecording && !isAudioPlaying) {
          startAutoRefreshCountdown(
            `Welcome back — refreshing in {s}s. You were away for ${durationLabel} and your session may have paused in the background.`,
            'resume-refresh',
            async () => {
              const healthy = await probeIdbHealth()
              if (healthy) {
                return false
              }
              return true
            },
          )
          return
        }

        if (awayMs > 120_000 && isRecording) {
          toastRef.current.showToast(
            'You were away while recording. Stopping and saving your recording for safety.',
            'warning',
            4000,
          )
          toastRef.current.showToast(
            "During recording, please don’t leave the screen. You can type a note and submit from the app instead.",
            'info',
            6000,
          )
          pendingRefreshAfterSaveRef.current = { durationLabel }
          void runRecoveryOnce().finally(() => {
            window.dispatchEvent(new Event(RECOVERY_STOP_RECORDING_EVENT))
          })
          return
        }
      }
    }

    const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
      if (isIndexedDbInternalError(event.reason)) {
        void runRecoveryOnce()
      }
    }

    window.addEventListener(RECOVERY_EVENT, handleRecoveryEvent)
    window.addEventListener('pageshow', handlePageShow)
    document.addEventListener('visibilitychange', handleVisibilityChange)
    window.addEventListener(RECORDING_SAVED_EVENT, handleRecordingSaved)
    window.addEventListener('unhandledrejection', handleUnhandledRejection)
    return () => {
      window.removeEventListener(RECOVERY_EVENT, handleRecoveryEvent)
      window.removeEventListener('pageshow', handlePageShow)
      document.removeEventListener('visibilitychange', handleVisibilityChange)
      window.removeEventListener(RECORDING_SAVED_EVENT, handleRecordingSaved)
      window.removeEventListener('unhandledrejection', handleUnhandledRejection)
      activeReloadHandleRef.current?.cancel()
      activeReloadHandleRef.current = null
      clearPendingAutoReload()
    }
  }, [])

  useEffect(() => {
    void recoverOrphanedDrafts()
      .then((count) => {
        if (count > 0) {
          toastRef.current.showToast(
            count === 1
              ? 'A recording that was interrupted has been recovered and is uploading.'
              : `${count} interrupted recordings have been recovered and are uploading.`,
            'info',
            6000,
          )
        }
      })
      .catch((err) => {
        console.warn('[recovery] Orphaned draft recovery failed', err)
      })
  }, [])

  return (
    <RecoveryContext.Provider value={{ isRecovering }}>
      {children}
      {isRecovering ? (
        <div
          className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/70 backdrop-blur-sm"
          role="alertdialog"
          aria-modal="true"
        >
          <div className="rounded-2xl bg-navy-light px-6 py-4 text-center text-white shadow-2xl border border-white/10 max-w-sm">
            <p className="text-lg font-semibold">Waking up… please wait</p>
            <p className="mt-2 text-sm text-white/80">
              We’re restoring storage and audio access after a pause. This will close shortly.
            </p>
          </div>
        </div>
      ) : null}
    </RecoveryContext.Provider>
  )
}

export function useRecovery(): RecoveryContextValue {
  const ctx = useContext(RecoveryContext)
  if (!ctx) {
    throw new Error('useRecovery must be used within RecoveryProvider')
  }
  return ctx
}

export function requestRecovery(): void {
  if (typeof window === 'undefined') return
  window.dispatchEvent(new Event(RECOVERY_EVENT))
}
